import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Gem, Loader, Lock, Crown, CheckCircle } from 'lucide-react';

const SPIN_MS = 4500;
const SPIN_EASE = 'cubic-bezier(0.12, 0.75, 0.18, 1)';

/** Cases / couleurs / dimensions — ne pas modifier la logique métier */
const PRIZES = [
  { type: 'win', product: 'DRY', amount: '2G', label: '2G DRY' },
  null,
  null,
  { type: 'win', product: 'COINS', amount: 10, label: '10 COINS' },
  null,
  null,
  null,
  { type: 'win', product: 'CALI', amount: '2G', label: '2G CALI' },
  null,
  null,
];
const WINNING_INDEXES = [0, 3, 7];
const LOSING_INDEXES = [1, 2, 4, 5, 6, 8, 9];

const WIN_COLORS = {
  fill0: 'rgba(125, 211, 252, 0.5)',
  fill1: 'rgba(96, 197, 240, 0.45)',
  fill2: 'rgba(79, 195, 247, 0.5)',
  stroke: 'rgba(125, 211, 252, 0.9)',
};
const LOSE_COLORS = {
  fill0: 'rgba(55, 65, 81, 0.6)',
  fill1: 'rgba(31, 41, 55, 0.7)',
  fill2: 'rgba(17, 24, 39, 0.65)',
  stroke: 'rgba(75, 85, 99, 0.7)',
};

async function fetchWithTimeout(url, options = {}, timeout = 10000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error?.name === 'AbortError') throw new Error('Timeout: La requête a pris trop de temps');
    throw error;
  }
}

function segmentPath(cx, cy, r, startDeg, endDeg) {
  const toRad = (d) => (d * Math.PI) / 180;
  const a0 = toRad(startDeg);
  const a1 = toRad(endDeg);
  const x1 = cx + r * Math.cos(a0);
  const y1 = cy + r * Math.sin(a0);
  const x2 = cx + r * Math.cos(a1);
  const y2 = cy + r * Math.sin(a1);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
}

const glassCard = {
  background: 'linear-gradient(160deg, rgba(74,144,226,0.22) 0%, rgba(30,60,120,0.38) 45%, rgba(10,20,60,0.55) 100%)',
  backdropFilter: 'blur(40px) saturate(260%) brightness(1.1)',
  WebkitBackdropFilter: 'blur(40px) saturate(260%) brightness(1.1)',
  border: '1.5px solid rgba(107,163,232,0.38)',
  boxShadow: '0 12px 48px rgba(0,0,0,0.45), 0 0 40px rgba(74,144,226,0.14), inset 0 1px 0 rgba(255,255,255,0.28)',
};

export default function TreasureWheel({
  onBack,
  turns,
  onTurnUsed,
  telegramChatId,
  BACKEND_URL,
  onBonusClaimed,
  onWheelVariantChosen,
  t = (k) => (k === 'claimBtn' ? 'Récupérer' : k),
}) {
  const uid = useId().replace(/:/g, '');
  const winGradId = `tw-win-${uid}`;
  const loseGradId = `tw-lose-${uid}`;
  const rimGradId = `tw-rim-${uid}`;

  const [isSpinning, setIsSpinning] = useState(false);
  const [availableTurns, setAvailableTurns] = useState(turns || 1);
  const [showWinModal, setShowWinModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [wonPrize, setWonPrize] = useState(null);
  const [showCaliVariantModal, setShowCaliVariantModal] = useState(false);

  const wheelRef = useRef(null);
  const rotationRef = useRef(0);
  const spinTimerRef = useRef(null);
  const spinningRef = useRef(false);

  useEffect(() => {
    setAvailableTurns(Math.max(turns || 0, 0));
  }, [turns]);

  useEffect(() => () => {
    if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
  }, []);

  const sectionAngle = 360 / PRIZES.length;

  const spinWheel = useCallback(() => {
    if (spinningRef.current || isSpinning || availableTurns <= 0) return;
    if (!wheelRef.current) return;

    spinningRef.current = true;
    const turnsLeft = Math.max(0, availableTurns - 1);
    if (onTurnUsed) onTurnUsed(turnsLeft, true);

    setIsSpinning(true);
    setAvailableTurns(turnsLeft);

    const isWin = Math.random() < 0.30;
    const targetIndex = isWin
      ? WINNING_INDEXES[Math.floor(Math.random() * WINNING_INDEXES.length)]
      : LOSING_INDEXES[Math.floor(Math.random() * LOSING_INDEXES.length)];

    const targetAngle = targetIndex * sectionAngle + sectionAngle / 2;
    const randomSpins = 5 + Math.floor(Math.random() * 3);
    const current = rotationRef.current;
    const normalized = ((current % 360) + 360) % 360;
    const desired = (randomSpins * 360) + (360 - targetAngle);
    const delta = desired - normalized;
    const next = current + delta;
    rotationRef.current = next;

    const el = wheelRef.current;
    el.style.transition = `transform ${SPIN_MS}ms ${SPIN_EASE}`;
    el.style.transform = `rotate(${next}deg)`;

    if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
    spinTimerRef.current = setTimeout(() => {
      spinningRef.current = false;
      setIsSpinning(false);
      if (isWin && PRIZES[targetIndex]) {
        setWonPrize(PRIZES[targetIndex]);
        setShowWinModal(true);
      }
    }, SPIN_MS + 40);
  }, [availableTurns, isSpinning, onTurnUsed, sectionAngle]);

  const processBonusClaim = async (variantLabel = null) => {
    if (!wonPrize) return;

    const productToBonusKey = {
      DRY: 'DRY',
      'Premium Mousse': 'MOUSS',
      MOUSS: 'MOUSS',
      CALI: 'CALI',
      'Canada Cali': 'CALI',
      COINS: 'COINS',
      Cali: 'CALI',
    };
    const bonusKey = productToBonusKey[wonPrize.product] || wonPrize.product;

    if (typeof onWheelVariantChosen === 'function' && variantLabel) {
      onWheelVariantChosen({ product: bonusKey, variantLabel });
    }

    if (!telegramChatId) {
      const localStorageKey = `wheelBonus_${bonusKey}`;
      const existingBonus = localStorage.getItem(localStorageKey);
      const bonusAmount = parseFloat(wonPrize.amount);
      const newBonus = existingBonus ? parseFloat(existingBonus) + bonusAmount : bonusAmount;
      localStorage.setItem(localStorageKey, newBonus.toString());
      setShowWinModal(false);
      setShowClaimModal(true);
      return;
    }

    try {
      const bonusAmount = parseFloat(wonPrize.amount);
      const response = await fetchWithTimeout(`${BACKEND_URL}/client/add-wheel-bonus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatId: telegramChatId,
          product: bonusKey,
          amount: bonusAmount,
        }),
      }, 10000);

      if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
      const data = await response.json();
      if (data.success) {
        if (onBonusClaimed) onBonusClaimed(bonusKey, bonusAmount);
        setShowWinModal(false);
        setShowClaimModal(true);
      } else {
        throw new Error(data.message || 'Erreur lors de la sauvegarde du bonus');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du bonus:', error);
      const localStorageKey = `wheelBonus_${bonusKey}`;
      const existingBonus = localStorage.getItem(localStorageKey);
      const bonusAmount = parseFloat(wonPrize.amount);
      const newBonus = existingBonus ? parseFloat(existingBonus) + bonusAmount : bonusAmount;
      localStorage.setItem(localStorageKey, newBonus.toString());
      setShowWinModal(false);
      setShowClaimModal(true);
    }
  };

  const handleClaimPrize = () => {
    if (!wonPrize) return;
    if (wonPrize.product === 'Cali' || wonPrize.product === 'CALI' || wonPrize.product === 'Canada Cali') {
      setShowCaliVariantModal(true);
      return;
    }
    let variantLabel = null;
    if (wonPrize.product === 'DRY') variantLabel = 'DRY🍫';
    else if (wonPrize.product === 'Premium Mousse' || wonPrize.product === 'MOUSS') variantLabel = 'Jaune Mousse';
    processBonusClaim(variantLabel);
  };

  const handleCaliVariantSelect = (variantLabel) => {
    setShowCaliVariantModal(false);
    processBonusClaim(variantLabel);
  };

  const disabled = isSpinning || availableTurns <= 0;
  const size = 100;
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 2.2;

  const spinLabel = isSpinning
    ? 'La roue tourne…'
    : availableTurns <= 0
      ? 'Déjà joué'
      : `Tourner (${availableTurns})`;

  return (
    <div className="tw-page">
      <img
        src="/emoji/SWISS3.png"
        alt=""
        aria-hidden
        className="tw-bg-img"
      />
      <div className="tw-bg-veil" aria-hidden />

      <style>{`
        .tw-page {
          --tw-nav: var(--device-nav-height, calc(96px + env(safe-area-inset-bottom, 0px)));
          --tw-safe-top: var(--gs-safe-top, env(safe-area-inset-top, 0px));
          --tw-side: max(14px, env(safe-area-inset-left, 0px), env(safe-area-inset-right, 0px));
          position: relative;
          min-height: 100dvh;
          min-height: 100vh;
          overflow-x: hidden;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          box-sizing: border-box;
        }
        .tw-bg-img {
          position: fixed; inset: 0; width: 100%; height: 100%;
          object-fit: fill; pointer-events: none; z-index: 0;
          opacity: 0.85; filter: saturate(1.15) brightness(0.95);
        }
        .tw-bg-veil {
          position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background:
            radial-gradient(ellipse 90% 50% at 50% -8%, rgba(74,144,226,0.40) 0%, transparent 55%),
            linear-gradient(180deg, rgba(8,16,36,0.35) 0%, rgba(8,16,36,0.55) 100%);
        }
        .tw-shell {
          position: relative; z-index: 10;
          width: 100%;
          max-width: 440px;
          margin: 0 auto;
          min-height: 100dvh;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
          padding:
            calc(var(--tw-safe-top) + 10px)
            var(--tw-side)
            calc(var(--tw-nav) + 12px)
            var(--tw-side);
        }
        .tw-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-shrink: 0;
          margin-bottom: clamp(6px, 1.5vh, 12px);
        }
        .tw-icon-btn, .tw-chip {
          display: inline-flex; align-items: center; justify-content: center;
          min-height: 44px;
          border-radius: 14px;
          background: linear-gradient(135deg, rgba(74,144,226,0.28) 0%, rgba(30,60,140,0.42) 100%);
          border: 1.5px solid rgba(147,197,253,0.4);
          box-shadow: 0 4px 18px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.22);
          backdrop-filter: blur(22px) saturate(220%);
          -webkit-backdrop-filter: blur(22px) saturate(220%);
          color: #f0f9ff;
          -webkit-tap-highlight-color: transparent;
        }
        .tw-icon-btn {
          width: 44px; padding: 0; cursor: pointer;
        }
        .tw-icon-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .tw-chip {
          padding: 0 14px;
          font-size: clamp(11px, 3.2vw, 13px);
          font-weight: 800;
          letter-spacing: 0.03em;
          border-radius: 999px;
          white-space: nowrap;
        }
        .tw-main {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: clamp(8px, 1.8vh, 16px);
          min-height: 0;
          width: 100%;
        }
        .tw-copy {
          text-align: center;
          width: 100%;
          padding: 0 4px;
          flex-shrink: 0;
        }
        .tw-eyebrow {
          margin: 0 0 4px;
          font-size: clamp(9px, 2.6vw, 11px);
          font-weight: 800;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(147,197,253,0.85);
        }
        .tw-title {
          margin: 0;
          font-size: clamp(1.35rem, 5.5vw, 1.85rem);
          font-weight: 900;
          color: #f0f9ff;
          letter-spacing: -0.02em;
          line-height: 1.15;
          text-shadow: 0 2px 20px rgba(74,144,226,0.4);
        }
        .tw-sub {
          margin: 6px 0 0;
          font-size: clamp(11px, 3.2vw, 13px);
          color: rgba(191,219,254,0.72);
          font-weight: 600;
          line-height: 1.35;
          max-width: 28ch;
          margin-left: auto;
          margin-right: auto;
        }
        .tw-wheel-shell {
          position: relative;
          /* Roue bornée par largeur ET hauteur dispo (évite chevauchement navbar) */
          width: min(78vw, 48dvh, 340px);
          height: min(78vw, 48dvh, 340px);
          margin: 0 auto;
          border-radius: 50%;
          flex-shrink: 0;
        }
        @media (max-height: 700px) {
          .tw-wheel-shell {
            width: min(68vw, 40dvh, 280px);
            height: min(68vw, 40dvh, 280px);
          }
          .tw-sub { display: none; }
          .tw-hint { display: none; }
        }
        @media (max-height: 620px) {
          .tw-wheel-shell {
            width: min(58vw, 34dvh, 240px);
            height: min(58vw, 34dvh, 240px);
          }
          .tw-eyebrow { display: none; }
        }
        .tw-wheel-ring {
          position: absolute; inset: 0; border-radius: 50%;
          pointer-events: none;
          background: linear-gradient(145deg, rgba(191,219,254,0.55) 0%, rgba(74,144,226,0.25) 40%, rgba(30,60,140,0.45) 100%);
          padding: clamp(4px, 1.4vw, 7px);
          box-shadow:
            0 0 0 1px rgba(147,197,253,0.35),
            0 12px 40px rgba(0,0,0,0.45),
            0 0 50px rgba(74,144,226,0.28),
            inset 0 1px 0 rgba(255,255,255,0.45);
        }
        .tw-wheel-ring-inner {
          width: 100%; height: 100%; border-radius: 50%;
          background: rgba(8,16,36,0.55);
          backdrop-filter: blur(18px) saturate(200%);
          -webkit-backdrop-filter: blur(18px) saturate(200%);
          overflow: hidden;
          position: relative;
          box-shadow: inset 0 0 28px rgba(74,144,226,0.18);
        }
        .tw-wheel-spin {
          width: 100%; height: 100%;
          border-radius: 50%;
          will-change: transform;
          backface-visibility: hidden;
          transform: translateZ(0);
        }
        .tw-pointer {
          position: absolute;
          top: -4px; left: 50%;
          transform: translateX(-50%);
          z-index: 20;
          width: clamp(22px, 6vw, 28px);
          height: clamp(28px, 8vw, 36px);
          filter: drop-shadow(0 4px 12px rgba(74,144,226,0.55)) drop-shadow(0 2px 6px rgba(0,0,0,0.45));
        }
        .tw-center {
          position: absolute;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          z-index: 15;
          width: clamp(48px, 14vw, 72px);
          height: clamp(48px, 14vw, 72px);
          border-radius: 50%;
          overflow: hidden;
          border: 2px solid rgba(147,197,253,0.55);
          box-shadow: 0 0 24px rgba(74,144,226,0.45), inset 0 1px 0 rgba(255,255,255,0.35);
          background: linear-gradient(145deg, rgba(74,144,226,0.35), rgba(10,20,60,0.65));
        }
        .tw-tick {
          position: absolute; inset: 8%; border-radius: 50%;
          pointer-events: none;
          background: repeating-conic-gradient(
            from -9deg,
            rgba(255,255,255,0.14) 0deg 1.2deg,
            transparent 1.2deg 36deg
          );
          opacity: 0.55;
          mix-blend-mode: soft-light;
        }
        .tw-actions {
          width: 100%;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: auto;
          padding-top: clamp(8px, 1.5vh, 14px);
        }
        .tw-spin-btn {
          width: 100%;
          min-height: 48px;
          padding: clamp(12px, 2.8vw, 15px) 16px;
          border-radius: 16px;
          font-weight: 800;
          font-size: clamp(13px, 3.6vw, 15px);
          color: #f0f9ff;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          -webkit-tap-highlight-color: transparent;
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }
        .tw-spin-btn:disabled { cursor: not-allowed; }
        .tw-hint {
          width: 100%;
          border-radius: 14px;
          padding: 10px 12px;
          text-align: center;
          background: linear-gradient(160deg, rgba(74,144,226,0.18) 0%, rgba(30,60,120,0.28) 100%);
          border: 1px solid rgba(107,163,232,0.32);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
        }
        .tw-hint p {
          margin: 0;
          font-size: clamp(10px, 2.9vw, 12px);
          font-weight: 600;
          color: #bfdbfe;
          line-height: 1.4;
        }
        @media (min-width: 768px) {
          .tw-shell { padding-bottom: 28px; }
          .tw-wheel-shell {
            width: min(360px, 42vh);
            height: min(360px, 42vh);
          }
        }
      `}</style>

      <div className="tw-shell">
        <div className="tw-top">
          <button
            type="button"
            className="tw-icon-btn"
            onClick={onBack}
            disabled={isSpinning}
            aria-label="Retour"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="tw-chip">
            {availableTurns > 0 ? `${availableTurns} tour${availableTurns > 1 ? 's' : ''}` : 'Terminé'}
          </div>
        </div>

        <div className="tw-main">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="tw-copy"
          >
            <p className="tw-eyebrow">Global Strains</p>
            <h1 className="tw-title">Weekly Wheel </h1>
            <p className="tw-sub"></p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
            className="tw-wheel-shell"
          >
            <svg className="tw-pointer" viewBox="0 0 28 36" aria-hidden>
              <defs>
                <linearGradient id={`${uid}-ptr`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#e0f2fe" />
                  <stop offset="55%" stopColor="#4a90e2" />
                  <stop offset="100%" stopColor="#1e3a8a" />
                </linearGradient>
              </defs>
              <path
                d="M14 34 L2 8 Q14 2 26 8 Z"
                fill={`url(#${uid}-ptr)`}
                stroke="rgba(191,219,254,0.85)"
                strokeWidth="1.2"
              />
              <circle cx="14" cy="10" r="2.2" fill="rgba(255,255,255,0.85)" />
            </svg>

            <div className="tw-wheel-ring">
              <div className="tw-wheel-ring-inner">
                <div className="tw-tick" aria-hidden />
                <div
                  ref={wheelRef}
                  className="tw-wheel-spin"
                  style={{ transform: `rotate(${rotationRef.current}deg)` }}
                >
                  <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%" style={{ display: 'block' }}>
                    <defs>
                      <linearGradient id={winGradId} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={WIN_COLORS.fill0} />
                        <stop offset="50%" stopColor={WIN_COLORS.fill1} />
                        <stop offset="100%" stopColor={WIN_COLORS.fill2} />
                      </linearGradient>
                      <linearGradient id={loseGradId} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={LOSE_COLORS.fill0} />
                        <stop offset="50%" stopColor={LOSE_COLORS.fill1} />
                        <stop offset="100%" stopColor={LOSE_COLORS.fill2} />
                      </linearGradient>
                      <linearGradient id={rimGradId} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="rgba(191,219,254,0.35)" />
                        <stop offset="100%" stopColor="rgba(74,144,226,0.15)" />
                      </linearGradient>
                    </defs>
                    <circle cx={cx} cy={cy} r={radius + 0.4} fill={`url(#${rimGradId})`} opacity="0.35" />
                    {PRIZES.map((_, index) => {
                      const isWinning = WINNING_INDEXES.includes(index);
                      const start = index * sectionAngle - 90;
                      const end = (index + 1) * sectionAngle - 90;
                      return (
                        <path
                          key={index}
                          d={segmentPath(cx, cy, radius, start, end)}
                          fill={isWinning ? `url(#${winGradId})` : `url(#${loseGradId})`}
                          stroke={isWinning ? WIN_COLORS.stroke : LOSE_COLORS.stroke}
                          strokeWidth={isWinning ? 0.55 : 0.35}
                          strokeLinejoin="round"
                          style={isWinning ? { filter: 'drop-shadow(0 0 1.2px rgba(125,211,252,0.7))' } : undefined}
                        />
                      );
                    })}
                    <circle cx={cx} cy={cy} r={14} fill="rgba(8,16,36,0.55)" stroke="rgba(147,197,253,0.25)" strokeWidth="0.4" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="tw-center">
              <img
                src="/emoji/logoapp1.png"
                alt="GLOBAL STRAINS"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                draggable={false}
              />
            </div>
          </motion.div>
        </div>

        <div className="tw-actions">
          <motion.button
            type="button"
            className="tw-spin-btn"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={spinWheel}
            disabled={disabled}
            whileTap={disabled ? undefined : { scale: 0.97 }}
            style={{
              opacity: disabled && !isSpinning ? 0.72 : 1,
              background: disabled
                ? 'linear-gradient(135deg, rgba(55,65,81,0.7), rgba(31,41,55,0.85))'
                : 'linear-gradient(135deg, rgba(74,144,226,0.55) 0%, rgba(30,80,180,0.72) 55%, rgba(10,40,120,0.78) 100%)',
              border: disabled
                ? '1.5px solid rgba(148,163,184,0.35)'
                : '1.5px solid rgba(147,197,253,0.55)',
              boxShadow: disabled
                ? 'none'
                : '0 8px 28px rgba(74,144,226,0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {isSpinning ? <Loader className="w-5 h-5 animate-spin flex-shrink-0" /> : null}
            {!isSpinning && availableTurns <= 0 ? <Lock className="w-5 h-5 flex-shrink-0" /> : null}
            {!isSpinning && availableTurns > 0 ? <Gem className="w-5 h-5 flex-shrink-0" /> : null}
            <span>{spinLabel}</span>
          </motion.button>

          <div className="tw-hint">
            <p>Lots : GC , 2G offert & plus...</p>
          </div>
        </div>
      </div>

      {/* Modales — même flux, chrome liquid glass */}
      <AnimatePresence>
        {showWinModal && wonPrize && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center"
            style={{
              zIndex: 'var(--gs-z-modal, 1000)',
              padding: 'max(12px, env(safe-area-inset-top)) max(14px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(14px, env(safe-area-inset-left))',
            }}
          >
            <div className="absolute inset-0" style={{ background: 'rgba(4,8,20,0.72)', backdropFilter: 'blur(14px)' }} />
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 18 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              style={{ ...glassCard, borderRadius: 24, padding: 'clamp(18px,4vw,28px)', maxWidth: 380, width: '100%', position: 'relative', textAlign: 'center', maxHeight: 'min(88dvh, 640px)', overflowY: 'auto' }}
            >
              <div style={{
                width: 72, height: 72, margin: '0 auto 16px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, rgba(125,211,252,0.45), rgba(74,144,226,0.35))',
                boxShadow: '0 0 28px rgba(125,211,252,0.45)',
              }}>
                <Crown className="w-9 h-9 text-white" />
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 900, color: '#f0f9ff' }}>Félicitations !</h2>
              <p style={{ margin: '0 0 6px', color: 'rgba(226,232,240,0.85)', fontWeight: 600 }}>Vous avez gagné</p>
              <p style={{
                margin: '0 0 22px', fontSize: 24, fontWeight: 900,
                background: 'linear-gradient(135deg, #7dd3fc, #4fc3f7)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                {wonPrize.label}
              </p>
              <button
                type="button"
                onClick={handleClaimPrize}
                style={{
                  width: '100%', padding: '14px', borderRadius: 16, fontWeight: 800, color: '#fff',
                  background: 'linear-gradient(135deg, rgba(74,144,226,0.55), rgba(30,80,180,0.75))',
                  border: '1.5px solid rgba(147,197,253,0.5)', cursor: 'pointer',
                }}
              >
                {t('claimBtn')}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCaliVariantModal && wonPrize && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 'var(--gs-z-modal, 1000)' }}
          >
            <div className="absolute inset-0" style={{ background: 'rgba(4,8,20,0.75)', backdropFilter: 'blur(14px)' }} />
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              style={{ ...glassCard, borderRadius: 28, padding: 26, maxWidth: 380, width: '100%', position: 'relative', textAlign: 'center' }}
            >
              <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 900, color: '#f0f9ff' }}>Choisis ta variété de Cali</h2>
              <p style={{ margin: '0 0 16px', color: 'rgba(191,219,254,0.8)', fontSize: 14 }}>
                Tu as gagné <strong>{wonPrize.amount}</strong> de Cali.
              </p>
              {[
                ['WHITE RUNZ🌺', 'linear-gradient(135deg,#f59e0b,#ea580c)'],
                ['DOSIDOS CANADA🇨🇦', 'linear-gradient(135deg,#22c55e,#059669)'],
                ['GRIZZLYBOYZ 🇺🇸', 'linear-gradient(135deg,#3b82f6,#1d4ed8)'],
              ].map(([label, bg]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => handleCaliVariantSelect(label)}
                  style={{
                    width: '100%', marginBottom: 10, padding: '12px 14px', borderRadius: 14,
                    fontWeight: 800, color: '#fff', border: 'none', cursor: 'pointer', background: bg,
                  }}
                >
                  {label}
                </button>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showClaimModal && wonPrize && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 'var(--gs-z-modal, 1000)' }}
          >
            <div className="absolute inset-0" style={{ background: 'rgba(4,8,20,0.72)', backdropFilter: 'blur(14px)' }} />
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              style={{ ...glassCard, borderRadius: 28, padding: 28, maxWidth: 380, width: '100%', position: 'relative', textAlign: 'center' }}
            >
              <div style={{
                width: 72, height: 72, margin: '0 auto 16px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, rgba(125,211,252,0.45), rgba(74,144,226,0.35))',
              }}>
                <CheckCircle className="w-9 h-9 text-white" />
              </div>
              <h2 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 900, color: '#f0f9ff' }}>Bonus enregistré !</h2>
              <p style={{ margin: '0 0 8px', color: 'rgba(226,232,240,0.9)', fontSize: 15 }}>
                Vous avez gagné <strong>{wonPrize.amount}</strong> de <strong>{wonPrize.product}</strong>
              </p>
              <p style={{ margin: '0 0 20px', color: 'rgba(191,219,254,0.75)', fontSize: 13, lineHeight: 1.45 }}>
                À votre prochaine commande du produit <strong>{wonPrize.product}</strong>, vous recevrez{' '}
                <strong style={{ color: '#86efac' }}>{wonPrize.amount} supplémentaire</strong> gratuitement !
              </p>
              <button
                type="button"
                onClick={() => { setShowClaimModal(false); setWonPrize(null); }}
                style={{
                  width: '100%', padding: '14px', borderRadius: 16, fontWeight: 800, color: '#fff',
                  background: 'linear-gradient(135deg, rgba(74,144,226,0.55), rgba(30,80,180,0.75))',
                  border: '1.5px solid rgba(147,197,253,0.5)', cursor: 'pointer',
                }}
              >
                Parfait !
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
